import { postComment } from "@/api/ticket";
import clipLIcon from "@/assets/images/clip_l.png";
import BlockModal from "@/components/BlockModal";
import ConfirmModal from "@/components/ConfirmModal";
import { customerInfo, TabStatus, Ticket } from "@/components/Ticket/types";
import * as Config from "@/config";
import { useBlockerStore } from "@/stores/blocker";
import { useLoadingStore } from "@/stores/loading";
import { useProjectStore } from "@/stores/project";
import { useUserStore } from "@/stores/user";
import useAutosizeTextArea from "@/utils/useAutosizeTextArea";
import { Fragment, useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { useFieldArray, useForm } from "react-hook-form";

interface Props {
  ticket?: Ticket;
  getTicket: () => void;
  customer?: customerInfo;
  tabStatuses: TabStatus[];
}

export interface PostComment {
  data_id?: number;
  customer_id?: number;
  data_ticket_id: number;
  is_inquiry_user: boolean;
  created_by: number;
  comment: string;
  ticket_destinations_id: number;
  attached_files?: {
    original_filename: string;
    file_content: string;
  }[];
}

const DEFAULT_FORM_VALUES: Partial<PostComment> = {
  comment: '',
  ticket_destinations_id: -1,
};

interface ModalMessage {
  submit?: boolean;
  message?: string;
}

export default function MessageInputBox({
                                          ticket,
                                          getTicket,
                                          customer,
                                          tabStatuses
                                        }: Props) {
  const setLoading = useLoadingStore((state) => state.setLoading);
  const user = useUserStore((state) => state.user);
  const [isBlocker, setBlocker] = useBlockerStore((state) => [
    state.isBlocker,
    state.setBlocker,
  ]);
  const [selectedProject, selectedProjectCategory] = useProjectStore(
    (state) => [state.project, state.projectCategory]
  );

  const isCloseStatus = (() => {
    if (!tabStatuses || tabStatuses.length === 0 || !ticket) return false;
    const maxCode = Math.max(...tabStatuses.map(status => parseInt(status.code, 10)));
    const maxStatusString = `${maxCode}:${tabStatuses.find(status => parseInt(status.code, 10) === maxCode)?.status}`;
    return ticket.status === maxStatusString;
  })();

  const {
    register,
    trigger,
    watch,
    control,
    reset,
    clearErrors,
    getValues,
    setValue,
    formState: {errors},
  } = useForm<PostComment>({
    reValidateMode: "onSubmit",
    defaultValues: DEFAULT_FORM_VALUES
  });
  const {fields, append, remove} = useFieldArray({
    name: "attached_files",
    control,
  });
  const watchTextArea = watch("comment");
  const watchFiles = watch("attached_files");

  useAutosizeTextArea("ticket-comment-textarea", watchTextArea);
  const [modalMessage, setModalMessage] = useState<ModalMessage>({});

  const onFileChange = (files: FileList) => {
    remove();
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result;
        append({original_filename: file.name, file_content: String(base64)});
      };
      reader.readAsDataURL(file);
    });
  };

  const onClickSend = () => {
    const setMessage = () => {
      if (watchTextArea.length == 0) return {message: Config.MESSAGE_NO_E66}
      return {submit: true, message: Config.MESSAGE_NO_E41}
    }
    setModalMessage(setMessage);
  };

  const onConfirm = () => {
    modalMessage.submit && onSubmit();
    setModalMessage({});
  };

  const onCancel = () => {
    setModalMessage({});
  };

  const onSubmit = () => {
    clearErrors();
    const data = {
      ...getValues(),
      customer_id: Number(customer?.id),
      data_ticket_id: Number(ticket?.data_ticket_id),
      is_inquiry_user: true,
      created_by: Number(user?.id),
    };
    trigger().then((isValid) => {
      if (!isValid) {
        return;
      }
      setLoading(true);
      postComment(data)
        .then((result) => {
          getTicket();
        })
        .catch((error) => {
          setLoading(false);
        })
        .finally(() => {
          reset(DEFAULT_FORM_VALUES);
          remove();
        });
    });
  };

  useEffect(() => {
    const bool =
      !!(watchFiles && watchFiles.length > 0) ||
      !!(watchTextArea && watchTextArea.length > 0);
    setBlocker(bool);
  }, [watchFiles, watchTextArea]);

  return (
    <>
      <div className="message-input-box">
        <textarea
          id="ticket-comment-textarea"
          {...register("comment", {
            required: Config.MESSAGE_NO_E08,
            maxLength: {value: 5000, message: Config.MESSAGE_NO_E35(5000)},
          })}
          rows={3}
        />
        {errors.comment && (
          <p className="precaution">{errors.comment.message}</p>
        )}
        <hr/>
        <div className="uk-flex uk-flex-middle">
          <FileUploader
            classes="file-drop"
            name={"files"}
            handleChange={(files: any) => onFileChange(files)}
            multiple={true}
            children={
              <>
                {fields.length > 0 ? (
                  <div className="uk-flex">
                    {fields.map((file, index) => (
                      <Fragment key={index}>
                        <img
                          src={clipLIcon}
                          width={22}
                          height={22}
                          className="uk-margin-small-right"
                        />
                        {file.original_filename}
                      </Fragment>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="uk-flex uk-flex-column">
                      <div className="uk-flex">
                        <img
                          src={clipLIcon}
                          width={22}
                          height={22}
                          className="uk-margin-small-right"
                        />
                        添付ファイルがある場合にはクリックして選択するか、ファイルをドロップしてください。
                      </div>
                      <div className="uk-flex uk-margin-remove-top uk-margin-remove-bottom ticket-msg text-sm">
                        複数ファイル選択可能：合計容量は10MBまでとなります。
                      </div>
                    </div>
                  </>
                )}
              </>
            }
          />
          <button
            className="uk-button--m uk-button-refer"
            onClick={onClickSend}
            disabled={isCloseStatus}
          >
            送信
          </button>
        </div>
        <BlockModal/>
        <ConfirmModal
          text={modalMessage.message ?? ""}
          confirmButtonText="OK"
          isShow={!!modalMessage.message}
          onConfirm={onConfirm}
          onCancel={modalMessage.submit ? onCancel : undefined}
        />
      </div>
    </>
  );
}
